import type { Stage, AdminProgress } from "@/hooks/useAdminAuth";
import { SERVICE_PLAN_CONFIG } from "@/pages/admin/progress/constants";

interface StageListProps {
  stages: Stage[];
  progress: AdminProgress[];
  selectedStage: number | null;
  clientServicePlan: string;
  clientJointStim: boolean;
  loading: boolean;
  hasExpensePerm: boolean;
  onSelectStage: (stageId: number) => void;
}

export default function StageList({
  stages, progress, selectedStage, clientServicePlan, clientJointStim, loading, hasExpensePerm, onSelectStage,
}: StageListProps) {
  const config = SERVICE_PLAN_CONFIG[clientServicePlan];

  const filteredStages = stages.filter((stage) => {
    if (!config) return false;
    const inRange = stage.id >= config.range[0] && stage.id <= config.range[1];
    if (!inRange) return false;
    if (stage.description?.includes("仅适用联合促排") || stage.name?.includes("适用联合促排")) return clientJointStim === true;
    return true;
  });

  const doneCount = progress.filter((p) =>
    p.stage_id >= (config?.range[0] || 0) && p.stage_id <= (config?.range[1] || 0)
  ).filter((p) => p.status === "completed" || p.status === "in_progress").length;

  return (
    <div className="lg:col-span-1 bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[#1a3a5c]">阶段列表</h3>
        <span className="text-[11px] text-gray-400">{filteredStages.length > 0 ? `${doneCount} / ${filteredStages.length} 个阶段` : "0 个阶段"}</span>
      </div>
      <div className="max-h-[600px] overflow-y-auto">
        {loading ? (
          <div className="p-6 text-center"><div className="w-8 h-8 border-2 border-[#1a3a5c] border-t-transparent rounded-full animate-spin mx-auto"></div></div>
        ) : filteredStages.length === 0 ? (
          <div className="p-6 text-center text-gray-400 text-sm"><i className="ri-list-check-3 text-2xl mb-2 block"></i>该客户暂无阶段数据</div>
        ) : (
          filteredStages.map((stage, idx) => {
            const displayOrder = idx + 1;
            const p = progress.find((x) => x.stage_id === stage.id);
            const isSelected = selectedStage === stage.id;
            const isCompleted = p?.status === "completed";
            const isInProgress = p?.status === "in_progress";
            return (
              <button key={stage.id} onClick={() => onSelectStage(stage.id)} className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors cursor-pointer ${isSelected ? "bg-[#1a3a5c]/5 border-l-[3px] border-[#c9a96e]" : "hover:bg-gray-50 border-l-[3px] border-transparent"}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium shrink-0 ${isCompleted ? "bg-[#c9a96e] text-white" : isInProgress ? "bg-[#1a3a5c] text-white" : "bg-gray-100 text-gray-400"}`}>
                  {isCompleted ? <i className="ri-check-line"></i> : displayOrder}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm truncate ${isSelected ? "font-semibold text-[#1a3a5c]" : "text-gray-700"}`}>
                    {stage.name}
                    {stage.is_expense && hasExpensePerm && (<span className="ml-1.5 text-[#c9a96e]" title="含支出管理"><i className="ri-money-cny-circle-line text-[11px]"></i></span>)}
                  </p>
                </div>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium shrink-0 ${isCompleted ? "bg-[#c9a96e]/10 text-[#c9a96e]" : isInProgress ? "bg-[#1a3a5c]/10 text-[#1a3a5c]" : "bg-gray-100 text-gray-400"}`}>
                  {isCompleted ? "已完成" : isInProgress ? "进行中" : "待开始"}
                </span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}